"use client";

import Image from "next/image";
import Link from "next/link";
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
  type KeyboardEvent,
} from "react";
import { useAuth } from "@/components/AuthProvider";
import CardNexusConnectPanel from "@/components/CardNexusConnectPanel";
import CollectionImportPanel from "@/components/CollectionImportPanel";
import { cardnexusFetch } from "@/lib/cardnexus/browser";
import {
  importCollectionFromCardnexus,
  type CnImportResult,
} from "@/lib/cardnexus/import-collection";
import { mergeLocalProductMap } from "@/lib/cardnexus/mapping";
import { DOMAIN_COLORS } from "@/lib/domains";
import { cardMatchesQuery } from "@/lib/card-search";
import {
  listInventory,
  setInventoryQuantity,
  type InventoryEntry,
} from "@/lib/local-store";
import { cardnexusSearchUrl } from "@/lib/merchant-links";
import type { CardRecord } from "@/lib/types";

type CnLine = {
  inventoryId: string;
  productId: number;
  quantity: number;
  cardId: string | null;
  finish: string;
};

type CnPrice = {
  productId: number;
  low: number | null;
  currency: string | null;
  listingCount: number | null;
};

function CloudHint() {
  const { user, loading } = useAuth();
  if (loading || user) return null;
  return (
    <p className="mt-3 text-sm text-amber-200/80">
      Stockage local uniquement.{" "}
      <Link href="/compte" className="underline decoration-amber-400/50 underline-offset-2 hover:text-amber-100">
        Crée un compte
      </Link>{" "}
      pour synchroniser en ligne.
    </p>
  );
}

function compareBySetThenNumber(a: CardRecord, b: CardRecord): number {
  const setA = (a.setId ?? "").toUpperCase();
  const setB = (b.setId ?? "").toUpperCase();
  if (setA !== setB) {
    if (!setA) return 1;
    if (!setB) return -1;
    return setA.localeCompare(setB);
  }
  const numA = a.collectorNumber;
  const numB = b.collectorNumber;
  if (numA == null && numB == null) return a.name.localeCompare(b.name);
  if (numA == null) return 1;
  if (numB == null) return -1;
  if (numA !== numB) return numA - numB;
  return a.name.localeCompare(b.name);
}

function QtyControls({
  quantity,
  onChange,
  disabled,
}: {
  quantity: number;
  onChange: (next: number) => void;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState(String(quantity));

  useEffect(() => {
    setDraft(String(quantity));
  }, [quantity]);

  function commit(raw: string) {
    const digits = raw.replace(/\D/g, "");
    const next = digits === "" ? 0 : Math.min(999, Math.max(0, Number(digits)));
    setDraft(String(next));
    if (next !== quantity) onChange(next);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      setDraft(String(quantity));
      e.currentTarget.blur();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      onChange(Math.min(999, quantity + 1));
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      onChange(Math.max(0, quantity - 1));
    }
  }

  return (
    <div className="mt-2 flex items-center justify-between gap-1">
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(Math.max(0, quantity - 1))}
        className="h-7 w-7 shrink-0 rounded-lg bg-white/10 text-sm hover:bg-white/15 disabled:opacity-40"
        aria-label="Diminuer"
      >
        −
      </button>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        disabled={disabled}
        value={draft}
        onChange={(e) => setDraft(e.target.value.replace(/\D/g, "").slice(0, 3))}
        onBlur={() => commit(draft)}
        onFocus={(e) => e.currentTarget.select()}
        onKeyDown={onKeyDown}
        className="h-7 w-10 rounded-lg border border-white/10 bg-black/40 text-center text-sm font-bold tabular-nums outline-none focus:border-cyan-500/50 disabled:opacity-40"
        aria-label="Quantité"
      />
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(Math.min(999, quantity + 1))}
        className="h-7 w-7 shrink-0 rounded-lg bg-white/10 text-sm hover:bg-white/15 disabled:opacity-40"
        aria-label="Augmenter"
      >
        +
      </button>
    </div>
  );
}

function formatPrice(p: CnPrice | undefined): string {
  if (!p || p.low == null) return "—";
  const cur = p.currency === "EUR" || !p.currency ? "€" : p.currency;
  return `${p.low.toFixed(2)}${cur}`;
}

export default function CollectionView() {
  const { user } = useAuth();
  const cnEnabled = Boolean(user);
  const [cards, setCards] = useState<CardRecord[]>([]);
  const [inventory, setInventory] = useState<InventoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  const [domainFilter, setDomainFilter] = useState("All");
  const [ownedOnly, setOwnedOnly] = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [cnConnected, setCnConnected] = useState(false);
  const [cnBusy, setCnBusy] = useState(false);
  const [cnError, setCnError] = useState<string | null>(null);
  const [cnSyncInfo, setCnSyncInfo] = useState<string | null>(null);
  const [lineByCardId, setLineByCardId] = useState<Map<string, CnLine>>(new Map());
  const [productByCardId, setProductByCardId] = useState<Map<string, number>>(new Map());
  const [pricesByProduct, setPricesByProduct] = useState<Map<number, CnPrice>>(new Map());
  const [qtyBusyId, setQtyBusyId] = useState<string | null>(null);
  /** Lignes CardNexus sans carte RiftHub correspondante (nouveaux sets, scellé…) */
  const [unmatchedLines, setUnmatchedLines] = useState<CnImportResult["lines"]>([]);

  const deferredSearch = useDeferredValue(search);
  const qtyMap = useMemo(
    () => new Map(inventory.map((e) => [e.cardId, e.quantity])),
    [inventory],
  );

  const loadPrices = useCallback(async (productIds: number[]) => {
    const unique = [...new Set(productIds.filter(Boolean))];
    // Une requête CardNexus par lot de 200 produits côté serveur
    for (let i = 0; i < unique.length; i += 200) {
      const chunk = unique.slice(i, i + 200);
      try {
        const data = await cardnexusFetch<{ prices: CnPrice[] }>(
          `/api/cardnexus/prices?productIds=${chunk.join(",")}`,
        );
        setPricesByProduct((prev) => {
          const next = new Map(prev);
          for (const p of data.prices ?? []) next.set(p.productId, p);
          return next;
        });
      } catch {
        /* non bloquant (quota CardNexus…) */
        break;
      }
    }
  }, []);

  const applyCnImport = useCallback(
    (result: CnImportResult) => {
      const lineMap = new Map<string, CnLine>();
      const prodMap = new Map<string, number>();

      for (const m of result.mirror) {
        prodMap.set(m.cardId, m.productId);
        lineMap.set(m.cardId, {
          inventoryId: m.inventoryId || "",
          productId: m.productId,
          quantity: m.quantity,
          cardId: m.cardId,
          finish: "Standard",
        });
      }
      for (const line of result.lines) {
        if (!line.cardId) continue;
        const prev = lineMap.get(line.cardId);
        if (!prev || !prev.inventoryId) {
          lineMap.set(line.cardId, {
            inventoryId: line.inventoryId,
            productId: line.productId,
            quantity: prev?.quantity ?? line.quantity,
            cardId: line.cardId,
            finish: line.finish,
          });
        }
        if (!prodMap.has(line.cardId)) prodMap.set(line.cardId, line.productId);
      }

      setLineByCardId(lineMap);
      setProductByCardId(prodMap);
      setUnmatchedLines(result.lines.filter((l) => !l.cardId));
      setInventory(result.entries);
      if (result.entries.length) setOwnedOnly(true);

      if (result.matched > 0) {
        setCnSyncInfo(
          result.unmatched > 0
            ? `Sync OK · ${result.matched} cartes · ${result.unmatched} ligne(s) non reconnue(s)`
            : `Sync OK · ${result.matched} cartes depuis CardNexus`,
        );
      } else if (result.count > 0) {
        setCnSyncInfo(
          `${result.count} ligne(s) CardNexus mais aucune carte reconnue dans le catalogue RiftHub.`,
        );
      } else {
        setCnSyncInfo("Inventaire CardNexus vide pour Riftbound.");
      }

      setCnConnected(true);
      void loadPrices([...prodMap.values()]);
    },
    [loadPrices],
  );

  const refreshFromCardnexus = useCallback(async () => {
    setCnBusy(true);
    setCnError(null);
    setCnSyncInfo(null);
    try {
      const result = await importCollectionFromCardnexus({ refresh: true });
      applyCnImport(result);
    } catch (err) {
      setCnError(err instanceof Error ? err.message : "Sync CardNexus impossible");
    } finally {
      setCnBusy(false);
    }
  }, [applyCnImport]);

  useEffect(() => {
    fetch("/api/cards")
      .then((r) => r.json())
      .then((data: { cards: CardRecord[] }) => setCards(data.cards ?? []))
      .catch(() => setCards([]))
      .finally(() => setLoading(false));
    setInventory(listInventory());
    const onSync = () => setInventory(listInventory());
    window.addEventListener("rifthub:storage-sync", onSync);
    return () => window.removeEventListener("rifthub:storage-sync", onSync);
  }, []);

  const onCnConnectionChange = useCallback((connected: boolean) => {
    setCnConnected(connected);
    if (!connected) {
      setLineByCardId(new Map());
      setPricesByProduct(new Map());
      setUnmatchedLines([]);
      setCnSyncInfo(null);
    }
  }, []);

  const onCollectionImported = useCallback(
    (result: CnImportResult) => {
      applyCnImport(result);
    },
    [applyCnImport],
  );

  const types = useMemo(() => {
    const set = new Set(cards.map((c) => c.type).filter(Boolean) as string[]);
    return ["All", ...[...set].sort()];
  }, [cards]);

  const domains = useMemo(() => {
    const set = new Set(cards.flatMap((c) => c.domains));
    return ["All", ...[...set].sort()];
  }, [cards]);

  const filtered = useMemo(() => {
    return cards
      .filter((c) => {
        if (typeFilter !== "All" && c.type !== typeFilter) return false;
        if (domainFilter !== "All" && !c.domains.includes(domainFilter)) return false;
        const qty = qtyMap.get(c.id) ?? 0;
        if (ownedOnly && qty <= 0) return false;
        return cardMatchesQuery(c, deferredSearch);
      })
      .sort(compareBySetThenNumber);
  }, [cards, deferredSearch, typeFilter, domainFilter, ownedOnly, qtyMap]);

  const ownedCount = inventory.reduce((s, e) => s + e.quantity, 0);
  const uniqueOwned = inventory.filter((e) => e.quantity > 0).length;

  async function setQty(card: CardRecord, next: number) {
    const clamped = Math.max(0, Math.min(999, next));
    if (!cnEnabled || !cnConnected) {
      setInventory(setInventoryQuantity(card.id, clamped));
      return;
    }

    setQtyBusyId(card.id);
    setCnError(null);
    try {
      const existing = lineByCardId.get(card.id);
      const productId = productByCardId.get(card.id);

      if (clamped <= 0) {
        if (existing?.inventoryId || existing) {
          await cardnexusFetch("/api/cardnexus/inventory", {
            method: "DELETE",
            body: JSON.stringify({
              ...(existing.inventoryId ? { inventoryId: existing.inventoryId } : {}),
              cardId: card.id,
            }),
          });
        }
        setLineByCardId((prev) => {
          const n = new Map(prev);
          n.delete(card.id);
          return n;
        });
        setInventory(setInventoryQuantity(card.id, 0));
        return;
      }

      if (existing?.inventoryId) {
        const res = await cardnexusFetch<{ line: CnLine }>("/api/cardnexus/inventory", {
          method: "PATCH",
          body: JSON.stringify({ inventoryId: existing.inventoryId, quantity: clamped }),
        });
        if (res.line) {
          setLineByCardId((prev) => new Map(prev).set(card.id, res.line));
          if (res.line.productId) {
            setProductByCardId((prev) => new Map(prev).set(card.id, res.line.productId));
            mergeLocalProductMap([{ cardId: card.id, productId: res.line.productId }]);
          }
        }
      } else {
        const res = await cardnexusFetch<{ line: CnLine | null }>("/api/cardnexus/inventory", {
          method: "POST",
          body: JSON.stringify({
            cardId: card.id,
            quantity: clamped,
            productId,
            card: {
              id: card.id,
              name: card.name,
              setId: card.setId,
              collectorNumber: card.collectorNumber,
              riftboundId: card.riftboundId,
            },
          }),
        });
        if (res.line) {
          setLineByCardId((prev) => new Map(prev).set(card.id, res.line!));
          setProductByCardId((prev) => new Map(prev).set(card.id, res.line!.productId));
          mergeLocalProductMap([{ cardId: card.id, productId: res.line.productId }]);
          void loadPrices([res.line.productId]);
        }
      }
      setInventory(setInventoryQuantity(card.id, clamped));
    } catch (err) {
      setCnError(err instanceof Error ? err.message : "MàJ CardNexus impossible");
    } finally {
      setQtyBusyId(null);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center gap-3 text-gray-400">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
        Chargement…
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wider text-cyan-400/80">Inventaire</p>
          <h1 className="mt-1 text-4xl font-black">
            <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
              Tes copies
            </span>
          </h1>
          <p className="mt-2 text-gray-500">
            {ownedCount} copies · {uniqueOwned} uniques
            {cnConnected ? " · source CardNexus" : ""}
          </p>
          <CloudHint />
        </div>
        <div className="flex flex-wrap gap-2">
          {cnEnabled && cnConnected && (
            <button
              type="button"
              disabled={cnBusy}
              onClick={() => void refreshFromCardnexus()}
              className="rounded-2xl border border-cyan-400/30 bg-cyan-500/15 px-5 py-3 text-sm font-bold text-cyan-200 hover:bg-cyan-500/25 disabled:opacity-50"
            >
              {cnBusy ? "Sync…" : "Rafraîchir CardNexus"}
            </button>
          )}
          <Link
            href="/galerie"
            className="rounded-2xl border border-white/10 bg-white/5 px-5 py-3 text-sm font-bold hover:bg-white/10"
          >
            Galerie
          </Link>
          <button
            type="button"
            onClick={() => setShowImport((v) => !v)}
            className="rounded-2xl bg-gradient-to-r from-cyan-600 to-blue-600 px-5 py-3 text-sm font-bold shadow-lg shadow-cyan-500/20"
          >
            {showImport ? "Fermer l'import" : "Importer CSV"}
          </button>
        </div>
      </div>

      {!user ? (
        <div className="mb-6 rounded-2xl border border-amber-500/20 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
          <Link href="/compte" className="font-semibold underline underline-offset-2">
            Connecte-toi à RiftHub
          </Link>{" "}
          puis lie CardNexus pour importer ta collection automatiquement.
        </div>
      ) : (
        <div className="mb-6">
          <CardNexusConnectPanel
            compact
            autoImport
            onConnectionChange={onCnConnectionChange}
            onCollectionImported={onCollectionImported}
          />
        </div>
      )}

      {cnEnabled && cnError && (
        <p className="mb-4 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-2 text-sm text-rose-300">
          {cnError}
        </p>
      )}
      {cnEnabled && !cnError && cnSyncInfo && (
        <p className="mb-4 rounded-xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-2 text-sm text-emerald-200/90">
          {cnSyncInfo}
        </p>
      )}

      {showImport && (
        <div className="mb-8">
          <CollectionImportPanel
            cards={cards}
            allowCardnexusApi={cnEnabled && cnConnected}
            onApplied={() => {
              setInventory(listInventory());
              setShowImport(false);
              setOwnedOnly(true);
            }}
          />
        </div>
      )}

      <div className="mb-6 flex flex-wrap gap-3">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Nom ou numéro (ex. OGN 195)…"
          className="min-w-[220px] flex-1 rounded-xl border border-white/10 bg-black/30 px-4 py-2.5 text-sm outline-none focus:border-cyan-500/50"
        />
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 text-sm"
        >
          {types.map((t) => (
            <option key={t} value={t}>
              {t === "All" ? "Tous types" : t}
            </option>
          ))}
        </select>
        <select
          value={domainFilter}
          onChange={(e) => setDomainFilter(e.target.value)}
          className="rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 text-sm"
        >
          {domains.map((d) => (
            <option key={d} value={d}>
              {d === "All" ? "Tous domaines" : d}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 text-sm text-gray-300">
          <input
            type="checkbox"
            checked={ownedOnly}
            onChange={(e) => setOwnedOnly(e.target.checked)}
            className="accent-cyan-500"
          />
          Possédées seulement
        </label>
      </div>

      {ownedOnly && filtered.length === 0 ? (
        <div className="glass rounded-2xl border border-white/5 p-12 text-center text-gray-400">
          Inventaire vide.{" "}
          {cnEnabled && cnConnected ? (
            <button
              type="button"
              className="text-cyan-300 underline"
              onClick={() => void refreshFromCardnexus()}
            >
              Rafraîchir depuis CardNexus
            </button>
          ) : (
            <>
              {!user ? (
                <Link href="/compte" className="text-cyan-300 underline">
                  Connecte-toi
                </Link>
              ) : (
                <span className="text-cyan-300">Lie CardNexus ci-dessus</span>
              )}
              {" ou "}
              <button
                type="button"
                className="text-cyan-300 underline"
                onClick={() => {
                  setShowImport(true);
                  setOwnedOnly(false);
                }}
              >
                importe un CSV
              </button>
              .
            </>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
          {filtered.map((card) => {
            const qty = qtyMap.get(card.id) ?? 0;
            const productId = productByCardId.get(card.id);
            const price = productId != null ? pricesByProduct.get(productId) : undefined;
            return (
              <div
                key={card.id}
                className={`rounded-2xl border p-2 ${
                  qty > 0 ? "border-cyan-500/30 bg-cyan-500/5" : "border-white/5 bg-white/[0.03]"
                }`}
              >
                {card.imageUrl ? (
                  <Image
                    src={card.imageUrl}
                    alt={card.name}
                    width={160}
                    height={224}
                    unoptimized
                    className="w-full rounded-xl"
                  />
                ) : (
                  <div className="flex aspect-[5/7] items-center justify-center rounded-xl bg-black/40 text-gray-600">
                    ?
                  </div>
                )}
                <p className="mt-2 truncate text-xs font-semibold">{card.name}</p>
                {(card.setId || card.collectorNumber != null) && (
                  <p className="truncate text-[10px] tabular-nums text-gray-500">
                    {[card.setId, card.collectorNumber].filter((v) => v != null && v !== "").join(" ")}
                  </p>
                )}
                <div className="mt-1 flex items-center justify-between gap-1">
                  <div className="flex flex-wrap gap-0.5">
                    {card.domains.slice(0, 2).map((d) => (
                      <span
                        key={d}
                        className="rounded px-1 text-[9px] font-bold"
                        style={{ color: DOMAIN_COLORS[d]?.text }}
                      >
                        {d}
                      </span>
                    ))}
                  </div>
                  {cnConnected && (
                    <a
                      href={cardnexusSearchUrl(card)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[10px] font-semibold text-violet-300 hover:text-violet-200"
                      title="Voir sur CardNexus"
                    >
                      {formatPrice(price)}
                    </a>
                  )}
                </div>
                <QtyControls
                  quantity={qty}
                  disabled={qtyBusyId === card.id}
                  onChange={(next) => void setQty(card, next)}
                />
              </div>
            );
          })}
        </div>
      )}

      {cnConnected && unmatchedLines.length > 0 && (
        <UnmatchedLines lines={unmatchedLines} />
      )}
    </div>
  );
}

/** Tout ce qui est dans l'inventaire CardNexus mais absent du catalogue RiftHub. */
function UnmatchedLines({ lines }: { lines: CnImportResult["lines"] }) {
  const groups = useMemo(() => {
    const byProduct = new Map<number, { line: CnImportResult["lines"][number]; quantity: number; details: string[] }>();
    for (const l of lines) {
      const g = byProduct.get(l.productId) ?? { line: l, quantity: 0, details: [] };
      g.quantity += l.quantity;
      g.details.push(
        [`${l.quantity}×`, l.finish, l.condition, l.language?.toUpperCase(), l.forSale ? "en vente" : null]
          .filter(Boolean)
          .join(" "),
      );
      byProduct.set(l.productId, g);
    }
    return [...byProduct.values()].sort((a, b) => (a.line.name ?? "").localeCompare(b.line.name ?? ""));
  }, [lines]);
  const total = groups.reduce((s, g) => s + g.quantity, 0);

  return (
    <details className="mt-10 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
      <summary className="cursor-pointer text-sm font-bold text-gray-200">
        Autres produits CardNexus · {groups.length} produit(s), {total} exemplaire(s)
        <span className="ml-2 font-normal text-gray-500">
          (non reconnus par le catalogue RiftHub : nouveaux sets, scellé…)
        </span>
      </summary>
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
        {groups.map(({ line, quantity, details }) => (
          <div key={line.productId} className="rounded-2xl border border-white/5 bg-black/20 p-2">
            {line.imageUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={line.imageUrl} alt={line.name ?? ""} loading="lazy" className="w-full rounded-xl" />
            ) : (
              <div className="flex aspect-[5/7] items-center justify-center rounded-xl bg-black/40 text-gray-600">?</div>
            )}
            <p className="mt-2 truncate text-xs font-semibold" title={line.name ?? undefined}>
              {line.name ?? `Produit #${line.productId}`}
            </p>
            <p className="truncate text-[10px] text-gray-500">
              {[line.expansionCode ?? line.expansionName, line.printNumber].filter(Boolean).join(" · ") || "—"}
            </p>
            <p className="mt-1 text-xs font-bold text-cyan-300">×{quantity}</p>
            <p className="text-[10px] text-gray-500">{details.join(" · ")}</p>
          </div>
        ))}
      </div>
    </details>
  );
}
